/** Sdílené kousky modulu Rozvoz (dříve Flotila). */
import { SKLO_SVETLE } from '../../lib/uiTokens';

export const SECTION_COLOR = '#0CC0DF';
export const SECTION_BG = 'rgba(12,192,223,0.12)';
export const CERVENA = '#EF4444';
export const ZELENA = '#059669';

/** Barvy zón v mapě — pořadí = výchozí barva n-té nové zóny. */
export const BARVY_ZON = ['#0CC0DF', '#E8457A', '#22C55E', '#F59E0B', '#8B5CF6', '#3B82F6', '#14B8A6', '#F97316'];

// Prahy „K dořešení" na jednom místě (norma §3)
export const PRAH_STK_DNI = 30;
export const PRAH_POJISTENI_DNI = 30;
export const PRAH_SERVIS_KM = 1000;
export const PRAH_BATERIE_V = 12.0;

export const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';
export const hlavicky = () => ({ 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() });
export const nacti = (url) => fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : Promise.reject(r)));

export const kc = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(Number(v)).toLocaleString('cs-CZ')} Kč`);
export const cislo = (v, d = 0) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: d, minimumFractionDigits: d }));
export const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('cs-CZ') : '—');
export const minut = (sec) => (sec == null ? '—' : `${Math.round(sec / 60)} min`);

export function stylKarty(isDark) {
    return isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
}

export function stylPole(isDark) {
    return {
        width: '100%', height: 34, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
}

/**
 * Co u aut nehraje — jedna funkce pro panel i mobilní řádek (norma §1g).
 * `problem` = jednoznačný problém (červeně), jinak neutrální upozornění.
 */
export function kDoreseniAut(vozidla = []) {
    const out = [];
    for (const v of vozidla) {
        if (v.stk_dni_do_expirace != null && v.stk_dni_do_expirace <= PRAH_STK_DNI) {
            const pozde = v.stk_dni_do_expirace < 0;
            out.push({ id: `stk-${v.id}`, vozidloId: v.id, problem: pozde || v.stk_dni_do_expirace <= 7,
                titulek: `${v.spz} · STK`, popis: pozde ? `prošla před ${-v.stk_dni_do_expirace} dny` : `končí za ${v.stk_dni_do_expirace} dní` });
        }
        if (v.pojisteni_dni_do_expirace != null && v.pojisteni_dni_do_expirace <= PRAH_POJISTENI_DNI) {
            const pozde = v.pojisteni_dni_do_expirace < 0;
            out.push({ id: `poj-${v.id}`, vozidloId: v.id, problem: pozde || v.pojisteni_dni_do_expirace <= 7,
                titulek: `${v.spz} · pojištění`, popis: pozde ? `skončilo před ${-v.pojisteni_dni_do_expirace} dny` : `končí za ${v.pojisteni_dni_do_expirace} dní` });
        }
        if (v.km_do_servisu != null && v.km_do_servisu <= PRAH_SERVIS_KM) {
            out.push({ id: `srv-${v.id}`, vozidloId: v.id, problem: v.km_do_servisu < 0,
                titulek: `${v.spz} · servis`, popis: v.km_do_servisu < 0 ? `přejeto o ${cislo(-v.km_do_servisu)} km` : `za ${cislo(v.km_do_servisu)} km` });
        }
        if (v.power_v != null && Number(v.power_v) < PRAH_BATERIE_V) {
            out.push({ id: `bat-${v.id}`, vozidloId: v.id, problem: true,
                titulek: `${v.spz} · baterie`, popis: `palubní napětí ${Number(v.power_v).toFixed(1)} V` });
        }
    }
    return out.sort((a, b) => Number(b.problem) - Number(a.problem));
}

/**
 * Návrh zóny z mřížky dojezdů: kolem bistra 72 paprsků po 5°, v každém
 * nejvzdálenější bod, kam se dojede do `minut`. Izochrona kolem jednoho
 * startu je zhruba hvězdicovitá, takže to dá rozumný první tvar, který
 * se pak doladí táhnutím vrcholů.
 *
 * @param {Array<[number, number, number]>} body - [lat, lng, sec]
 * @returns {Array<[number, number]>} polygon [lat, lng]
 */
export function navrhniZonuPodleDojezdu(body, bistro, minut, paprsku = 72) {
    if (!bistro || !body?.length) return [];
    const kx = Math.cos((bistro.lat * Math.PI) / 180);
    const nejdal = new Array(paprsku).fill(null);
    for (const [lat, lng, sec] of body) {
        if (sec > minut * 60) continue;
        const dx = (lng - bistro.lng) * kx;
        const dy = lat - bistro.lat;
        const d = Math.hypot(dx, dy);
        const uhel = (Math.atan2(dy, dx) + 2 * Math.PI) % (2 * Math.PI);
        const i = Math.floor((uhel / (2 * Math.PI)) * paprsku) % paprsku;
        if (!nejdal[i] || d > nejdal[i].d) nejdal[i] = { d, lat, lng };
    }
    const vrcholy = nejdal.filter(Boolean).map((b) => [Number(b.lat.toFixed(6)), Number(b.lng.toFixed(6))]);
    return vrcholy.length >= 3 ? vrcholy : [];
}

/** Pásmo dojezdu po 5 minutách → průhlednost (víc = blíž). */
export function pasmoDojezdu(sec) {
    const m = sec / 60;
    if (m <= 5) return 0.55;
    if (m <= 10) return 0.40;
    if (m <= 15) return 0.27;
    if (m <= 20) return 0.17;
    if (m <= 25) return 0.09;
    return 0.03;
}

/** Legenda pásem — stejné hodnoty jako `pasmoDojezdu`. */
export const PASMA_DOJEZDU = [[5, 0.55], [10, 0.40], [15, 0.27], [20, 0.17], [25, 0.09]];
